import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Padrão único das telas de Administração no celular.
 *
 * O uso real é no campo, no telefone — mas as telas de admin nasceram como
 * tabelas de desktop. Duas regras, que este arquivo existe para não deixar
 * ninguém reinventar:
 *
 * 1. **Abaixo de `md`, cada linha vira card; de `md` pra cima, a tabela de
 *    sempre.** `AdminLista` já é `md:hidden` e `AdminTabela` já é
 *    `hidden md:block` — não duplique os breakpoints na página.
 *
 * 2. **No celular esconde-se METADADO, nunca IDENTIDADE nem AÇÃO.** Por isso
 *    `identidade` é obrigatória e sempre `truncate`; `meta` é o que pode ser
 *    cortado (vai para a tela de edição); `acoes` fica sempre visível, à
 *    direita, com alvo de toque de 44px (`ALVO_TOQUE`).
 *
 * Nada de grid com colunas fixas em px no mobile: foi assim que a tela de
 * Atividades perdeu a coluna do nome — `1fr 120px 160px 40px` soma 320px de
 * colunas fixas, mais do que os ~284px que sobram num iPhone de 390px, e o
 * `1fr` colapsava pra zero. O `overflow-x-hidden` do AppLayout então cortava o
 * resto em vez de rolar, e a linha simplesmente sumia.
 */

/**
 * Alvo de toque, cabeçalho de página: a definição mora agora em
 * `components/layout/page-header.tsx` — `AdminPageHeader` virou `PageHeader`,
 * o cabeçalho único da plataforma. Reexportado aqui para não quebrar quem já
 * importava deste arquivo.
 */
export {
  ALVO_TOQUE,
  ALVO_TOQUE_BOTAO,
  PageHeader,
  AdminPageHeader,
} from "@/components/layout/page-header";

/** Lista em cards — só abaixo de `md`. */
export function AdminLista({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("md:hidden space-y-2", className)}>{children}</div>;
}

/** Container da tabela — só de `md` pra cima. Mantém o desktop como está. */
export function AdminTabela({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("hidden md:block rounded-lg border overflow-hidden", className)}>
      {children}
    </div>
  );
}

export interface AdminListaItemProps {
  /** Identificador primário — nunca é ocultado nem some por falta de espaço. */
  identidade: React.ReactNode;
  /** Metadado secundário (badges, texto muted). O que não couber vai pra edição. */
  meta?: React.ReactNode;
  /** Controles inline (switch, editar). Ficam legíveis mesmo com `atenuado`. */
  acoes?: React.ReactNode;
  /** Abre a edição ao tocar no card. Use quando editar é a única ação. */
  onClick?: () => void;
  /** Registro inativo/arquivado: esmaece só o conteúdo, nunca os controles. */
  atenuado?: boolean;
  testId?: string;
  className?: string;
}

/**
 * Uma linha da lista, em card.
 *
 * `atenuado` esmaece apenas identidade e metadado. Aplicar opacidade no card
 * inteiro — como a tela de Atividades fazia — apaga junto o interruptor, que é
 * justamente o controle usado pra reativar o registro.
 */
export function AdminListaItem({
  identidade,
  meta,
  acoes,
  onClick,
  atenuado,
  testId,
  className,
}: AdminListaItemProps) {
  const clicavel = Boolean(onClick);

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-xl border bg-card px-3 py-2.5",
        clicavel && "cursor-pointer hover:bg-muted/30 transition-colors",
        className
      )}
      onClick={onClick}
      onKeyDown={
        clicavel
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
      role={clicavel ? "button" : undefined}
      tabIndex={clicavel ? 0 : undefined}
      data-testid={testId}
    >
      <div className={cn("min-w-0 flex-1", atenuado && "opacity-50")}>
        <div className="font-medium text-sm truncate">{identidade}</div>
        {meta && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {meta}
          </div>
        )}
      </div>
      {acoes && (
        <div
          className="flex items-center gap-1 shrink-0"
          onClick={clicavel ? e => e.stopPropagation() : undefined}
        >
          {acoes}
        </div>
      )}
    </div>
  );
}
